import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { camelCase, cloneDeep, has, isEmpty, isFunction } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { useWatch } from 'react-hook-form';
import { NotFoundError } from '../pages/NotFoundPage';
import { hasAtLeastPermission, PERMISSION, requirePermission } from '../pages/sharing/utils';
import { NON_FIELD_ERRORS } from './ErrorToNotificationsLink';
import { extractEnabledFields, OVERRIDES_NAME } from './utils';

function useEntity(getEntityQuery, getResolverName, id, skip, onDoesNotExist) {
    const { showBoundary } = useErrorBoundary();

    const response = useQuery(getEntityQuery, {
        variables: { id },
        skip,
    });

    const { error: entityError } = response;

    useEffect(() => {
        if (!entityError) {
            return;
        }

        if (
            entityError.graphQLErrors.some(
                (subError) => subError.extensions.code === 'ENTITY_UNKNOWN'
            )
        ) {
            if (isFunction(onDoesNotExist)) {
                onDoesNotExist();
            }
            return;
        }

        showBoundary(entityError);
    }, [entityError, onDoesNotExist, showBoundary]);

    return useMemo(
        () => ({
            ...response,
            data: response.data?.[getResolverName] ?? null,
        }),
        [response, getResolverName]
    );
}

function applyMutationErrors(formController, mutationErrors, keyPrefix = 'apiError') {
    if (isEmpty(mutationErrors)) {
        return false;
    }

    let shouldFocus = true;
    mutationErrors.forEach(({ field, message }, index) => {
        if (field) {
            formController.rhfProps.setError(
                camelCase(field),
                { type: 'server', message },
                { shouldFocus }
            );
            shouldFocus = false;
            return;
        }

        formController.setGlobalError(`${keyPrefix}${index}`, message);
    });
    return true;
}

// All hooks that do things with a project are wrapped inside this component
// that will be included when needed. Using a component instead of just a hook
// is to overcome the hooks cannot be optional violation.
function OptionalParentDataLink(dataLinkProps) {
    const { formController } = dataLinkProps;
    const { showBoundary } = useErrorBoundary();

    const { getParentEntityQuery, getParentResolverName, parentEntityFieldName } =
        formController.queries;

    const parentEntityId = useWatch({
        defaultValue: null,
        name: parentEntityFieldName,
        disable: !parentEntityFieldName,
    });

    // Everyone who can see a measuring point has at least reference access to its project, so a
    // project that cannot be found means the permission cache is broken.
    const onParentDoesNotExist = useCallback(() => {
        showBoundary(new Error(`Missing project "${parentEntityId}" of this measuring point.`));
    }, [parentEntityId, showBoundary]);

    const { data: parentEntityData } = useEntity(
        getParentEntityQuery,
        getParentResolverName,
        parentEntityId,
        !getParentEntityQuery || !parentEntityId,
        onParentDoesNotExist
    );

    useEffect(() => {
        // Null until the project has loaded, so the form does not mistake it for a project without
        // values.
        const parentValues = parentEntityId ? parentEntityData : null;

        formController.setParentValues(
            parentValues && formController.convertDataIntoFormValues(parentValues)
        );
    }, [parentEntityId, parentEntityData, formController]);
    return null;
}

function GraphQLDataLink(dataLinkProps) {
    const { showBoundary } = useErrorBoundary();
    const graphQLClient = useApolloClient();

    const { setLoading, formController } = dataLinkProps;

    const {
        objectName,
        getEntityQuery,
        getResolverName,
        updateEntityQuery,
        updateEntityQueryName,
        updatePrivateNotesQuery,
        updatePrivateNotesResponseKey,
        updatePrivateNotesEntityKey,
        createEntityQuery,
        createEntityQueryName,
        getParentEntityQuery,
    } = formController.queries;

    const isAddMode = !formController.id;

    // If there is a parent entity query, this entity has an optional parent (project).
    const entityHasOptionalParent = !!getParentEntityQuery;

    const [mutationQuery, mutationQueryName] = isAddMode
        ? [createEntityQuery, createEntityQueryName]
        : [updateEntityQuery, updateEntityQueryName];

    const [submitMutate] = useMutation(mutationQuery);

    const [privateNotesMutate] = useMutation(updatePrivateNotesQuery);
    const privateNotesRootErrorPaths = useRef([]);
    const skipNextEntityReset = useRef(false);
    const savePrivateNotesOnly = useCallback(
        async ({ id, privateNotes }) => {
            if (privateNotesRootErrorPaths.current.length) {
                formController.rhfProps.clearErrors(privateNotesRootErrorPaths.current);
                privateNotesRootErrorPaths.current = [];
            }

            try {
                const result = await privateNotesMutate({
                    variables: { id, privateNotes },
                    fetchPolicy: 'no-cache',
                });
                const data = result.data?.[updatePrivateNotesResponseKey];

                if (!data) {
                    throw new Error(`Missing ${updatePrivateNotesResponseKey} mutation payload.`);
                }

                const savedEntity = data[updatePrivateNotesEntityKey];
                const savedPrivateNotes = savedEntity.privateNotes ?? '';
                const cacheId = graphQLClient.cache.identify(savedEntity);

                if (cacheId) {
                    const cacheWasUpdated = graphQLClient.cache.modify({
                        id: cacheId,
                        fields: {
                            privateNotes(existingPrivateNotes) {
                                if (existingPrivateNotes === savedPrivateNotes) {
                                    return existingPrivateNotes;
                                }

                                skipNextEntityReset.current = true;
                                return savedPrivateNotes;
                            },
                        },
                    });

                    if (!cacheWasUpdated) {
                        skipNextEntityReset.current = false;
                    }
                }

                return savedPrivateNotes;
            } catch (error) {
                privateNotesRootErrorPaths.current = [`${NON_FIELD_ERRORS}.serverError`];
                formController.handleGenericServerError(error);
                return null;
            }
        },
        [
            formController,
            graphQLClient.cache,
            privateNotesMutate,
            updatePrivateNotesEntityKey,
            updatePrivateNotesResponseKey,
        ]
    );

    const onSubmit = useCallback(
        (values, _event, { addAnotherAfterSave = false, saveButtonKey = null } = {}) => {
            if (!formController.startSave(saveButtonKey)) {
                // We're already saving, no need to do it again!
                return;
            }

            const payload = cloneDeep(values);

            formController.createReadOnlyFields().forEach((name) => {
                delete payload[name];
            });

            delete payload.sensor;
            delete payload.alarmValue;
            delete payload.coordinates;
            delete payload.city;
            delete payload.swarmType;

            if (entityHasOptionalParent) {
                payload.overriddenFields = extractEnabledFields(payload[OVERRIDES_NAME]);
            }
            delete payload[OVERRIDES_NAME];

            if (has(payload, 'project') && payload.project === '') {
                payload.project = null;
            }

            delete payload.permission;

            // Set a default value of 0 for (null)/('') values.
            if (has(payload, 'dailyValueSettings')) {
                payload.dailyValueSettings.triggers.forEach((item, index) => {
                    if (!item.trigger) payload.dailyValueSettings.triggers[index].trigger = 0;
                    if (!item.days) payload.dailyValueSettings.triggers[index].days = 0;
                });
            }

            if (has(payload, 'ldenGuideline')) {
                if (payload.ldenGuideline !== 'LdenGuidelineCustom') {
                    payload.notificationConfig.ldenSchedules = [];
                }
            }

            if (has(payload, 'soundClipAlarmSource')) {
                if (payload.soundClipAlarmSource === 'disabled') {
                    delete payload.tracePostTrigger;
                    delete payload.tracePreTrigger;
                    delete payload.traceSaveLevel;
                }
            }

            // Apply unit conversions.
            const convertedPayload = formController.convertConfigValues(payload, true);

            const mutationOptions = {
                variables: {
                    input: convertedPayload,
                },
                onCompleted(result) {
                    formController.setSaveIsInProgress(false);
                    const data = result?.[mutationQueryName];

                    const mutationErrors = data?.errors;

                    if (applyMutationErrors(formController, mutationErrors)) {
                        return;
                    }

                    formController.onSubmitSuccessful$.next({
                        id: data[objectName].id,
                        addAnotherAfterSave,
                        entityName: values.name,
                        warnings: (data.warnings ?? []).map(({ message }) => message),
                    });
                },
                onError(error) {
                    formController.setSaveIsInProgress(false);
                    formController.handleGenericServerError(error);
                },
            };

            if (!isAddMode) {
                // Add ID for the update.
                mutationOptions.variables.input.id = formController.id;
            }

            submitMutate(mutationOptions);
        },
        [
            isAddMode,
            entityHasOptionalParent,
            submitMutate,
            formController,
            mutationQueryName,
            objectName,
        ]
    );

    useEffect(() => {
        formController.setOnSubmitFunction(onSubmit);
    }, [formController, onSubmit]);

    useEffect(() => {
        formController.setPrivateNotesSaveFunction(savePrivateNotesOnly);
        return () => formController.setPrivateNotesSaveFunction();
    }, [formController, savePrivateNotesOnly]);

    const { loading: getEntityLoading, data: entityData } = useEntity(
        getEntityQuery,
        getResolverName,
        formController.id,
        isAddMode,
        () => {
            showBoundary(new NotFoundError());
        }
    );

    useEffect(() => {
        setLoading(() => getEntityLoading);
    }, [setLoading, getEntityLoading]);

    // Set form values when we received the data.
    useEffect(() => {
        if (!entityData) {
            return;
        }

        // Reference access lets a measuring point show its project, but the project's own page
        // needs view access. To the user, the page does not exist.
        const permission = requirePermission(
            entityData.permission,
            `${objectName} "${formController.id}"`
        );
        if (!hasAtLeastPermission(permission, PERMISSION.View)) {
            showBoundary(new NotFoundError());
            return;
        }

        if (skipNextEntityReset.current) {
            skipNextEntityReset.current = false;
            return;
        }

        formController.setValues(formController.convertDataIntoFormValues(entityData));
    }, [entityData, entityHasOptionalParent, formController, objectName, showBoundary]);

    return <>{getParentEntityQuery && <OptionalParentDataLink {...dataLinkProps} />}</>;
}

export default GraphQLDataLink;
